import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TrendingUp, Receipt, Info, Download, Loader2, X, Megaphone, CircleCheck, UserRound, Users } from "lucide-react";
import { api } from "@/lib/api";
import { CLP } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DateField } from "@/components/DateField";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { IncomeChart } from "@/components/IncomeChart";
import { MaestroReviewsCard } from "@/components/MaestroRating";

export const dmy = (s) => (s ? s.slice(0, 10).split("-").reverse().join("/") : "");

const InfoTip = ({ text, id }) => (
  <TooltipProvider delayDuration={100}><Tooltip><TooltipTrigger asChild>
    <button type="button" className="text-muted-foreground hover:text-primary" aria-label="Más información" data-testid={`info-${id}`}><Info className="w-3.5 h-3.5" /></button>
  </TooltipTrigger><TooltipContent className="max-w-xs">{text}</TooltipContent></Tooltip></TooltipProvider>
);

const InfoStat = ({ id, label, value, sub, icon: Icon, tip, big }) => (
  <Card className={`p-5 ${big ? "sm:col-span-2 lg:col-span-1" : ""}`} data-testid={`stat-${id}`}>
    <div className="flex items-center justify-between gap-2">
      <span className="text-sm text-muted-foreground flex items-center gap-1.5">{label}{tip && <InfoTip text={tip} id={id} />}</span>
      {Icon && <Icon className="w-4 h-4 text-primary shrink-0" />}
    </div>
    <p className={`font-display font-extrabold text-2xl mt-2 ${big ? "text-primary" : ""}`} data-testid={`stat-${id}-value`}>{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
  </Card>
);

const p2 = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
const RANGES = [
  ["this", "Este mes", () => { const t = new Date(); return [ymd(new Date(t.getFullYear(), t.getMonth(), 1)), ymd(t)]; }],
  ["last", "Mes pasado", () => { const t = new Date(); return [ymd(new Date(t.getFullYear(), t.getMonth() - 1, 1)), ymd(new Date(t.getFullYear(), t.getMonth(), 0))]; }],
  ["90", "Últimos 90 días", () => { const t = new Date(); const s = new Date(t); s.setDate(t.getDate() - 89); return [ymd(s), ymd(t)]; }],
];

const QuickRanges = ({ f, setF }) => (
  <div className="flex flex-wrap gap-2 sm:col-span-2 lg:col-span-4" data-testid="resumen-quick-ranges">
    {RANGES.map(([id, label, get]) => {
      const [from, to] = get();
      const on = f.from === from && f.to === to;
      return (
        <button key={id} type="button" onClick={() => setF({ ...f, from, to })} aria-pressed={on} data-testid={`resumen-range-${id}`}
          className={`rounded-full border px-3.5 h-9 sm:h-8 text-xs font-medium transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "hover:border-primary/60 hover:text-primary"}`}>
          {label}
        </button>
      );
    })}
  </div>
);

const Filters = ({ f, setF, workshops, onCsv, csvBusy }) => (
  <Card className="p-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1.4fr_auto] items-end" data-testid="resumen-filters">
    <QuickRanges f={f} setF={setF} />
    <div className="text-xs font-medium space-y-1"><span>Desde</span>
      <DateField value={f.from} max={f.to || undefined} onChange={(v) => setF({ ...f, from: v })} testId="resumen-from" placeholder="dd-mm-aaaa" floating /></div>
    <div className="text-xs font-medium space-y-1"><span>Hasta</span>
      <DateField value={f.to} min={f.from || undefined} onChange={(v) => setF({ ...f, to: v })} testId="resumen-to" placeholder="dd-mm-aaaa" floating /></div>
    <div className="text-xs font-medium space-y-1"><span>Taller</span>
      <Select value={f.ws || "all"} onValueChange={(v) => setF({ ...f, ws: v === "all" ? "" : v })}>
        <SelectTrigger className="h-9" data-testid="resumen-workshop"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="all">Todos los talleres</SelectItem>
          {workshops.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
      </Select></div>
    <div className="flex gap-2">
      {(f.from || f.to || f.ws) && <Button variant="ghost" size="sm" onClick={() => setF({ from: "", to: "", ws: "" })} data-testid="resumen-clear"><X className="w-4 h-4 mr-1" />Limpiar</Button>}
      <Button size="sm" onClick={onCsv} disabled={csvBusy} className="gap-1.5" data-testid="resumen-csv">
        {csvBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}Descargar CSV</Button>
    </div>
  </Card>
);

const toParams = (f) => ({ date_from: f.from || undefined, date_to: f.to || undefined, workshop_id: f.ws || undefined });

export function MaestroResumen() {
  const [f, setF] = useState({ from: "", to: "", ws: "" });
  const [st, setSt] = useState(null);
  const [workshops, setWorkshops] = useState([]);
  const [csvBusy, setCsvBusy] = useState(false);
  const [err, setErr] = useState(false);
  const [tries, setTries] = useState(0);
  useEffect(() => {
    setErr(false);
    api.get("/business/statement", { params: toParams(f) })
      .then((r) => { setSt(r.data); setWorkshops(r.data.workshops || []); })
      .catch(() => setErr(true));
  }, [f, tries]);

  const csv = async () => {
    setCsvBusy(true);
    try {
      const r = await api.get("/business/statement.csv", { params: toParams(f), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `resumen-vivotaller-${new Date().toISOString().slice(0, 10)}.csv` });
      a.click(); URL.revokeObjectURL(url);
      toast.success("Archivo CSV descargado");
    } catch { toast.error("No se pudo generar el archivo. Intenta nuevamente."); }
    finally { setCsvBusy(false); }
  };

  return (
    <div className="pt-6 space-y-6">
      <Filters f={f} setF={setF} workshops={workshops} onCsv={csv} csvBusy={csvBusy} />
      {err ? (
        <div className="py-10 text-center text-sm text-muted-foreground" data-testid="resumen-error">No pudimos cargar el resumen.
          <Button variant="link" onClick={() => setTries((t) => t + 1)} data-testid="resumen-retry">Reintentar</Button></div>
      ) : !st ? <div className="py-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <>
          <p className="text-xs text-muted-foreground -mt-2" data-testid="resumen-count">{st.reservations_count} reserva{st.reservations_count === 1 ? "" : "s"} de alumnos{f.from || f.to || f.ws ? " según los filtros" : ""}.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <InfoStat id="published" label="Talleres publicados" value={st.published_count} icon={Megaphone} tip="Talleres que publicaste usando una Ficha" />
            <InfoStat id="active" label="Talleres activos" value={st.active_count} icon={CircleCheck} tip="Talleres publicados que hoy están visibles para reservar" />
            <InfoStat id="unique-students" label="Alumnos únicos" value={st.unique_students} icon={UserRound} tip="Personas distintas que han reservado en tus talleres" />
            <InfoStat id="total-students" label="Alumnos en tus talleres" value={st.total_students} icon={Users} tip="Total de cupos reservados; un mismo alumno puede contar varias veces" />
            <InfoStat id="estimated" label="Ingresos estimados" value={CLP(st.estimated_income_clp)} icon={TrendingUp} big tip="Precio del taller al día de cada reserva × cupos. Los alumnos te pagan directamente" />
            <InfoStat id="vt-payments" label="Pagos a VIVOTaller" value={CLP(st.vt_payments_clp)} sub={`${st.vt_payments_count} compra${st.vt_payments_count === 1 ? "" : "s"} de Fichas · IVA incluido`} icon={Receipt} tip="Compras de Fichas pagadas en la plataforma de pago" />
          </div>
          <IncomeChart data={st.monthly} />
          <MaestroReviewsCard />
        </>
      )}
    </div>
  );
}
